import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import uPlotNS from "uplot";
import type uPlot from "uplot";

import { chartTheme } from "./chartTheme";
import { UPlotChart } from "./UPlotChart";
import { zoomPlugin, type ZoomWindow } from "./zoomPlugin";
import { timeAxisValues } from "./timeAxis";

export interface TimeSeriesTileProps {
  label: string;
  unit: string;
  header?: ReactNode;
  times: number[];
  values: (number | null)[];
  expected?: (number | null)[];
  height?: number;
  /** Clamp the x view, e.g. generation hours 06:00–19:00 (unix seconds). */
  xRange?: [number, number];
  /** The instant after which the plot has NO data, drawn as a boundary with the
   *  tail beyond it shaded.
   *
   *  Without it a feed that stopped at 11:20 leaves a flat run to the right edge
   *  that reads as "the plant produced nothing all afternoon" — a measured claim —
   *  when the truth is that nobody was listening. The line says which reading was
   *  the last real one; the shading says the rest is absence, not zero. */
  staleAfter?: number | null;
  /** How the primary series is drawn.
   *
   *  "line" (default) is the intraday curve: a continuous quantity sampled every
   *  five minutes, where the line between two samples is a real claim about what
   *  happened in between.
   *
   *  "bars" is for a DAILY series, where it is not: a day's energy is a total, one
   *  value per day, and drawing a line between two totals invents a trajectory
   *  through a day that was never measured. Bars say "these are separate buckets",
   *  which is what they are. */
  kind?: "line" | "bars";
  /** A second quantity on its OWN right-hand axis — irradiance (W/m²) beside power
   *  (kW), the pairing an O&M reader checks first: a dip in power with irradiance
   *  flat is a plant fault, the same dip with irradiance falling is a cloud. Two
   *  units cannot share one axis, so it gets its own scale and is drawn as a thin
   *  dashed line with no fill, behind the measured curve. */
  secondary?: { label: string; unit: string; values: (number | null)[] };
  /** Render the plot ALONE — no `.card` wrapper and no fallback title.
   *  For a caller that already provides the card and its heading, such as the
   *  P4 `.smtile`, where the default chrome would nest a card inside a card and
   *  print the label twice. */
  bare?: boolean;
}

/** The x-scale `range` function, reading its window through REFS.
 *
 *  Factored out and taking refs by construction, because the alternative is the one
 *  bug this component can have and it is invisible in review: `range` is installed
 *  on the uPlot instance ONCE and the instance outlives a change of window, so a
 *  function closing over the window it was built with keeps answering with that one
 *  for ever. Stepping to another day then drew nothing — but only on a REVISIT,
 *  since a day seen for the first time loads, shows a skeleton and remounts with a
 *  fresh closure. Taking refs makes the stale version impossible to write.
 */
export function makeXRange(
  zoomRef: { current: ZoomWindow | null },
  baseRef: { current: [number, number] | undefined },
) {
  return (_u: uPlot, dataMin: number, dataMax: number): [number, number] =>
    (zoomRef.current ?? baseRef.current ?? [dataMin, dataMax]) as [number, number];
}

/** The boundary past which there is no data, and the dead tail beyond it.
 *
 *  Drawn UNDER the series (a `draw` hook runs before the paths), so the curve is
 *  never obscured by its own annotation. */
function stalenessPlugin(at: number, theme: { axis: string; textMuted: string }): uPlot.Plugin {
  return {
    hooks: {
      draw: (u: uPlot) => {
        const x = u.valToPos(at, "x", true);
        const right = u.bbox.left + u.bbox.width;
        if (!Number.isFinite(x) || x >= right) return;
        const { ctx } = u;
        ctx.save();

        // The tail: absence, not zero.
        ctx.fillStyle = "rgba(128,128,128,0.07)";
        ctx.fillRect(x, u.bbox.top, right - x, u.bbox.height);

        // The boundary itself.
        ctx.strokeStyle = theme.axis;
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, u.bbox.top);
        ctx.lineTo(x, u.bbox.top + u.bbox.height);
        ctx.stroke();

        // Named, so the reader does not have to infer what the line means.
        const when = new Date(at * 1000).toLocaleTimeString(undefined, {
          hour: "2-digit", minute: "2-digit", hour12: false,
        });
        ctx.setLineDash([]);
        ctx.fillStyle = theme.textMuted;
        ctx.font = "600 10px var(--sk-font-text, sans-serif)";
        ctx.textBaseline = "top";
        const text = `no data after ${when}`;
        const w = ctx.measureText(text).width;
        // Inside the plot when there is room to its right, otherwise to its left,
        // so the label never runs off the canvas.
        ctx.fillText(text, right - x > w + 10 ? x + 5 : x - w - 5, u.bbox.top + 3);
        ctx.restore();
      },
    },
  };
}

/** Monotone-cubic interpolation between samples.
 *
 *  A five-minute power series drawn as straight segments reads as a jagged
 *  polyline; the quantity underneath is continuous, and the eye is being asked to
 *  do the smoothing. MONOTONE specifically, not a plain cubic: it is the spline
 *  that cannot overshoot its own data, so it never draws a peak higher than any
 *  reading or a dip below one. Any interpolation is a claim about the gaps — a
 *  straight line is too — and this is the claim that adds no value the meter did
 *  not report.
 *
 *  Built once: uPlot resolves the path builder at draw time, and a fresh function
 *  each render defeats that. */
const splinePaths = (uPlotNS as unknown as {
  paths: { spline: (o?: Record<string, unknown>) => unknown };
}).paths.spline() as never;

/** The area under the measured curve, fading out downward.
 *
 *  A flat tint reads as a second filled shape competing with the line; a fade
 *  keeps the line the subject and the area a weight beneath it. Built per draw
 *  because a canvas gradient is bound to the context and the plot's height. */
function areaFill(stroke: string) {
  return (u: uPlot) => {
    const { ctx } = u;
    const top = u.bbox.top;
    const bottom = top + u.bbox.height;
    const g = ctx.createLinearGradient(0, top, 0, bottom);
    g.addColorStop(0, withAlpha(stroke, 0.22));
    g.addColorStop(1, withAlpha(stroke, 0));
    return g;
  };
}

/** A colour at an alpha, whatever notation the token arrived in. Falls back to the
 *  flat theme fill rather than guessing at a format it cannot parse. */
function withAlpha(colour: string, alpha: number): string {
  const c = colour.trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const n = parseInt(hex[1]!, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(c);
  if (rgb) {
    const [r, g, b] = rgb[1]!.split(",").map((x) => x.trim());
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // A var() or a named colour: let the browser resolve it, with the alpha applied
  // by the canvas instead.
  return c;
}

/** Bars at 58% of their slot — the proportion `DailyBars` uses, so two bar charts
 *  in one product do not read as different densities. Built once: uPlot resolves
 *  the path builder at draw time and a new function each render defeats that. */
const barPaths = (uPlotNS as unknown as {
  paths: { bars: (o: Record<string, unknown>) => unknown };
}).paths.bars({ size: [0.58, Infinity] }) as never;

/** Over-layer cursor readout at the hovered sample (UX-006 §2).
 *
 *  With a second quantity it reads both, stacked and labelled — the whole reason
 *  for putting irradiance beside power is to compare them AT AN INSTANT, and a
 *  readout naming only one makes the reader eyeball the other off its axis. */
function tooltipPlugin(
  unit: string,
  measuredIdx: number,
  label: string,
  secondary?: { idx: number; label: string; unit: string },
): uPlot.Plugin {
  let tip: HTMLDivElement | null = null;
  return {
    hooks: {
      init: (u: uPlot) => {
        tip = document.createElement("div");
        Object.assign(tip.style, {
          position: "absolute", top: "2px", transform: "translateX(-50%)",
          padding: "2px 6px", font: "500 11px/1.4 var(--sk-font-text, sans-serif)",
          background: "var(--sk-surface, #fff)", color: "var(--sk-text, #111)",
          border: "1px solid var(--sk-border-subtle, #ddd)", borderRadius: "4px",
          whiteSpace: "nowrap", pointerEvents: "none", display: "none", zIndex: "10",
          lineHeight: "1.5", boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
        } as CSSStyleDeclaration);
        u.over.appendChild(tip);
      },
      setCursor: (u: uPlot) => {
        if (!tip) return;
        const idx = u.cursor.idx;
        const xv = idx != null ? u.data[0]?.[idx] : null;
        const yv = idx != null ? u.data[measuredIdx]?.[idx] : null;
        if (idx == null || xv == null || yv == null) { tip.style.display = "none"; return; }
        const t = new Date((xv as number) * 1000);
        const hh = t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
        const num = (v: number) => Math.round(v).toLocaleString();

        if (!secondary) {
          tip.textContent = `${hh} · ${num(yv as number)}${unit ? ` ${unit}` : ""}`;
        } else {
          const sv = u.data[secondary.idx]?.[idx];
          tip.replaceChildren();
          const time = document.createElement("div");
          time.style.fontWeight = "600";
          time.textContent = hh;
          tip.appendChild(time);
          const row = (name: string, u2: string, v: unknown, colour: string) => {
            const el = document.createElement("div");
            el.style.color = colour;
            // A missing sample says so rather than being dropped: a blank line
            // would read as the two quantities disagreeing about the instant.
            el.textContent = `${name} (${u2}) : ${typeof v === "number" ? num(v) : "—"}`;
            tip!.appendChild(el);
          };
          row(label, unit, yv, "var(--sk-color-accent, #2563eb)");
          row(secondary.label, secondary.unit, sv, "var(--sk-color-ink-3, #888)");
        }
        tip.style.left = `${u.valToPos(xv as number, "x")}px`;
        tip.style.display = "block";
      },
    },
  };
}

export function TimeSeriesTile({ label, unit, header, times, values, expected, height, xRange, bare, secondary, kind = "line", staleAfter }: TimeSeriesTileProps) {
  const theme = chartTheme();

  // ── Zoom (UX-006 §5.1/§6: click two points or drag; Esc or Reset returns) ──
  // The window is React state so the chip can show it, and a ref so the scale's
  // `range` can read it without the options object changing identity (a changed
  // options object no longer rebuilds the plot, by design).
  const [zoom, setZoom] = useState<ZoomWindow | null>(null);
  const zoomRef = useRef<ZoomWindow | null>(null);
  zoomRef.current = zoom;
  const plotRef = useRef<uPlot | null>(null);

  const handleZoom = useCallback((w: ZoomWindow) => setZoom(w), []);
  const handleReset = useCallback(() => setZoom(null), []);

  // A new base window means a new period was chosen; a zoom from the old one would
  // be meaningless (and could sit outside the new data entirely).
  const baseFrom = xRange?.[0];
  const baseTo = xRange?.[1];
  useEffect(() => setZoom(null), [baseFrom, baseTo]);

  // The base window goes through a REF for the same reason the zoom does, and the
  // reason is not symmetry — it is the one bug this component can have.
  //
  // `scales.x.range` is installed on the uPlot instance ONCE, when the plot is
  // built, and the plot is only rebuilt when its STRUCTURE changes. Two days of the
  // same chart have identical structure, so stepping from the 23rd to the 22nd and
  // back left the original function still closing over the 23rd's window: the
  // imperative `setScale` below asked for the right window, uPlot called `range` to
  // confirm it, and `range` answered with the old one. The 22nd's samples fell
  // outside it and the plot drew nothing — but only on a REVISIT, because a date
  // seen for the first time loads, shows a skeleton, and remounts the plot with a
  // fresh closure. A ref is read at draw time, so it cannot go stale.
  const xRangeRef = useRef<[number, number] | undefined>(undefined);
  xRangeRef.current = xRange;

  // Applied to the LIVE plot: the options are memoised on structure, so re-ranging
  // has to be imperative.
  useEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    const target = zoom ?? (baseFrom != null && baseTo != null ? [baseFrom, baseTo] : null);
    if (target) {
      plot.setScale("x", { min: target[0], max: target[1] });
      return;
    }
    const xs = plot.data[0];
    if (xs && xs.length) {
      plot.setScale("x", { min: Number(xs[0]), max: Number(xs[xs.length - 1]) });
    }
  }, [zoom, baseFrom, baseTo]);

  const data = useMemo<uPlot.AlignedData>(
    () => {
      const base = expected ? [times, expected, values] : [times, values];
      return (secondary ? [...base, secondary.values] : base) as uPlot.AlignedData;
    },
    [times, values, expected, secondary],
  );

  const options = useMemo<Omit<uPlot.Options, "width" | "height">>(() => {
    const axes: uPlot.Axis[] = [
      // X axis: no vertical gridlines (UX-006 §2). Labels read in the muted ink
      // and semibold so the ticks are legible; `stroke` colours the tick TEXT in
      // uPlot, so the faint theme.axis is kept only for the tick marks (see
      // PlanVsDelivered).
      {
        stroke: theme.textMuted,
        // 24-hour ticks, like every other time in the product.
        values: timeAxisValues,
        font: "600 12px var(--sk-font-text, sans-serif)",
        // At most one label per 90 px (CHART-SPECIFICATION §8 / UX-006). Without a
        // minimum spacing uPlot packs ticks to its ~50 px default, which overlapped
        // the HH:MM labels on a narrow plot (UX-REV-002 2.1, block AC-power chart).
        space: 90,
        grid: { show: false },
        ticks: { stroke: theme.axis },
      },
      // Y axis: horizontal gridlines, no axis line, unit as the axis label.
      {
        stroke: theme.textMuted,
        grid: { stroke: theme.grid, width: 1 },
        ticks: { show: false },
        ...(unit ? { label: unit, labelGap: 2, labelSize: 22, labelFont: "600 11px var(--sk-font-text, sans-serif)" } : {}),
      },
    ];

    // The second quantity's own axis, on the right. No gridlines of its own — two
    // sets of horizontal rules at different intervals turn the plot into graph
    // paper and neither can be read against.
    if (secondary) {
      axes.push({
        scale: "y2",
        side: 1,
        stroke: theme.textMuted,
        grid: { show: false },
        ticks: { show: false },
        label: secondary.unit,
        labelGap: 2,
        labelSize: 26,
        labelFont: "600 11px var(--sk-font-text, sans-serif)",
      });
    }

    const series: uPlot.Series[] = expected
      ? [
          {},
          { label: "expected", stroke: theme.reference, width: 1.5, dash: [4, 3], points: { show: false } },
          { label, stroke: theme.measured, width: 2, fill: theme.fill, points: { show: false } },
        ]
      : [
          {},
          // Measured line with a soft area fill under it (UX-006 §10.4 surface-2 fill).
          kind === "bars"
            ? {
                label,
                stroke: theme.measured,
                fill: theme.measured,
                width: 1,
                points: { show: false },
                // 58% of the slot, the same proportion the daily-bar chart uses, so
                // two bar charts in the product do not read as different densities.
                paths: barPaths,
              }
            : {
                label,
                stroke: theme.measured,
                width: 2,
                fill: areaFill(theme.measured),
                paths: splinePaths,
                points: { show: false },
              },
        ];

    if (secondary) {
      series.push({
        label: secondary.label,
        scale: "y2",
        stroke: theme.reference,
        width: 1,
        dash: [3, 3],
        paths: splinePaths,
        points: { show: false },
      });
    }

    const measuredIdx = expected ? 2 : 1;
    return {
      // The range answers from the zoom ref FIRST, so a user zoom and a pinned base
      // window (e.g. generation hours 06:00-19:00) coexist. The old
      // `range: () => xRange` pinned the axis outright — which is precisely what
      // cancelled uPlot's own drag-zoom and left the product with no zoom anywhere.
      scales: {
        x: {
          time: true,
          range: makeXRange(zoomRef, xRangeRef),
        },
        // Its own scale, so a 900 W/m² curve cannot squash a 4,000 kW one flat.
        ...(secondary ? { y2: { auto: true } } : {}),
      },
      legend: { show: false },
      // `setScale: false` — the plugin lifts the dragged selection into React rather
      // than letting uPlot re-range behind the component's back, so the window stays
      // single-sourced and the chip can show it.
      cursor: {
        x: true, y: false, points: { show: true },
        drag: { x: true, y: false, setScale: false },
      },
      plugins: [
        ...(staleAfter != null ? [stalenessPlugin(staleAfter, theme)] : []),
        tooltipPlugin(unit, measuredIdx, label,
                      secondary ? { idx: data.length - 1, label: secondary.label, unit: secondary.unit } : undefined),
        zoomPlugin({ onZoom: handleZoom, onReset: handleReset }),
      ],
      axes,
      series,
    };
  }, [label, unit, theme, expected, handleZoom, handleReset, secondary, data.length, kind, staleAfter]);

  const measured = values.filter((v) => v !== null).length;
  const summary =
    measured === 0 ? `${label}: no data in range.` : `${label}: ${measured} points over time (${unit}).`;

  // UX-006 §2 "Never a flat line at zero" / §5.4 "a sensor with no mapping shows Not
  // measured and an empty plot, never a zero line".
  //
  // A window whose values are EVERY ONE null used to draw a blank plot inside full
  // axes, which reads as "measured, and nothing happened" — the one reading the
  // data-honesty rules forbid. All ten call sites guard only `times.length === 0`,
  // so such a window reached the plot (QA, 25 Oct).
  //
  // An all-ZERO series is deliberately still drawn: at night the power genuinely IS
  // zero, and this renderer cannot tell a real zero from an unmapped channel. That
  // distinction belongs to the figure's served state, so suppressing it here would
  // hide real measurements — the opposite error.
  const hasFinite = values.some((v) => v !== null && Number.isFinite(v));
  if (!hasFinite) {
    const note = (
      <div className="sk-empty sk-empty--quiet" role="note">
        <span className="sk-empty__mark" aria-hidden="true">—</span>
        <span className="sk-empty__title">Not measured in this window</span>
      </div>
    );
    if (bare) return note;
    return (
      <div className="card">
        {header ?? <div className="mb-2 font-medium">{label}</div>}
        {note}
      </div>
    );
  }

  const plot = (
    <UPlotChart
      options={options}
      data={data}
      height={height ?? 160}
      ariaLabel={`${label} over time`}
      summary={summary}
      onPlot={(p) => {
        plotRef.current = p;
      }}
    />
  );

  // The range chip UX-006 §6 asks for: it names the window the zoom produced and
  // carries the way back out. Rendered only while zoomed, so an untouched chart
  // stays clean.
  const fmtX = (v: number) =>
    new Date(v * 1000).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  const chip = zoom ? (
    <div className="sk-chart-zoom">
      <span className="sk-chart-zoom__range">
        {fmtX(zoom[0])}–{fmtX(zoom[1])}
      </span>
      <button type="button" className="sk-chart-zoom__reset" onClick={handleReset}>
        Reset
      </button>
    </div>
  ) : null;

  if (bare) {
    return (
      <>
        {chip}
        {plot}
      </>
    );
  }

  return (
    <div className="card">
      {header ?? <div className="mb-2 font-medium">{label}</div>}
      {chip}
      {plot}
    </div>
  );
}
